import Vue from "vue";
import VueRouter from "vue-router";
import Landing from "../views/Landing.vue";
import axios from "axios";
import { i18n } from "../plugins/vueI18n";
import store from "../store";
import { Trans } from "../plugins/translation";
import moment from "moment";

Vue.use(VueRouter);
const routes = [
  {
    path: "/",
    redirect: i18n.locale,
  },
  {
    path: "/:lang",
    component: {
      render: (h) => h("router-view"),
    },
    children: [
      {
        path: "",
        name: "landing",
        component: Landing,
      },
      {
        path: "scheduling",
        // name: "scheduling",
        component: {
          render: (h) => h("router-view"),
        },

        children: [
          {
            path: "",
            name: "scheduling",
            component: () =>
              import(
                /* webpackChunkName: "scheduling" */
                "../views/AppointmentSchedulingForm.vue"
              ),
            meta: {
              requiresAuth: true,
            },
          },
          {
            path: "vaccine/:token?",
            name: "covid vaccine",
            component: () =>
              import(
                /* webpackChunkName: "covidLanding" */ "../views/COVIDLanding.vue"
              ),
          },
        ],
      },
      {
        path: "home",
        name: "home",
        component: () =>
          import(/* webpackChunkName: "home" */ "../views/WelcomeForm.vue"),
        meta: {
          requiresAuth: true,
        },
      },

      {
        path: "forms",
        component: {
          render: (h) => h("router-view"),
        },
        children: [
          {
            path: "",
            redirect: "/",
          },
          {
            path: ":id",
            name: "forms",
            component: () =>
              import(/* webpackChunkName: "forms" */ "../views/Forms.vue"),
          },
          {
            path: ":id/:form",
            name: "DynamicForm",
            props: true,
            component: () =>
              import(
                /* webpackChunkName: "dynamicForm" */ "../views/Forms.vue"
              ),
            beforeEnter: (to, from, next) => {
              if (!to.params.formId) {
                next({
                  name: "forms",
                  params: { id: to.params.id, lang: i18n.locale },
                });
              } else {
                axios
                  .get(`/forms/form/${to.params.formId}`)
                  .then((response) => {
                    if (response.data[0].completed_on) {
                      alert("Form has already been submitted");
                      next({
                        name: "forms",
                        params: { id: to.params.id, lang: i18n.locale },
                      });
                    } else if (
                      response.data[0].expires_on <= moment().toISOString()
                    ) {
                      next({
                        name: "forms",
                        params: { id: to.params.id, lang: i18n.locale },
                      });
                    } else {
                      to.params.receivedForm = response.data[0];
                      next();
                    }
                  })
                  .catch((error) => {
                    if (error.response.status == 404) {
                      next({
                        name: "landing",
                      });
                    }
                  });
              }
            },
          },
        ],
      },
      {
        path: "ics/:documentId?/:accessToken?",
        name: "ics",
        component: () => import("../components/ics/downloadPdf.vue"),
      },
      {
        path: "labs",
        component: {
          render: (h) => h("router-view"),
        },
        children: [
          {
            path: "",
            redirect: "/",
          },
          {
            path: "results/:token?",
            component: () =>
              import(
                /* webpackChunkName: "labLanding" */ "../views/LabLanding.vue"
              ),
          },
        ],
      },
      // {
      //   path: "telehealth",
      //   component: {
      //     render: (h) => h("router-view"),
      //   },
      //   children: [
      //     {
      //       path: "",
      //       component: () =>
      //         import(
      //           /* webpackChunkName: "home" */ "../components/telehealth/TelehealthPatient.vue"
      //         ),
      //     },
      //   ],
      // },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
  scrollBehavior(to, from, savedPosition) {
    return {
      x: 0,
      y: 0,
    };
  },
});
router.beforeEach((to, from, next) => {
  const lang = to.params.lang;
  if (!["en", "es"].includes(lang)) return next("en");
  if (i18n.locale !== lang) {
    i18n.locale = lang;
  }
  return next();
});
function initialPatientLoad() {
  if (!store.state.person.name) {
    store.commit("setLoader", { type: "patientLoad", isLoading: true });
    store.state.error.login.unassigned = false;
    const details = axios.get("person/details");
    const events = axios.get("appointments/events");
    const appointments = axios.get("appointments");
    const locations = axios.get("appointments/locations");

    return details
      .then((response) => {
        store.commit("setPerson", response.data);
        if (!response.data.pcp) {
          store.state.error.login.unassigned = true;
        }
      })
      .then(() => {
        return axios.all([appointments, events, locations]).then(
          axios.spread((...responses) => {
            const responseAppointments = responses[0];
            const responseEvents = responses[1];
            const responseLocations = responses[2];
            store.commit("setEvents", responseEvents.data);
            store.commit("setAppointments", responseAppointments.data || []);
            store.commit("setLocations", responseLocations.data);
          })
        );
      })
      .then(() => {
        axios.get("ics/documents").then((response) => {
          store.commit("setDocuments", response.data || []);
        });
        axios.get("appointments/recallplans").then((response) => {
          store.commit("setRecallPlans", response.data || []);
        });
      })
      .catch((error) => {
        throw new Error(error?.response || error);
      })
      .finally(() => {
        store.commit("setLoader", {
          type: "patientLoad",
          isLoading: false,
        });
      });
  }
  return Promise.resolve("state loaded");
}
router.beforeEach((to, from, next) => {
  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    to.name !== from.name
  ) {
    // this route requires auth, check if logged in
    // if not, redirect to login page.
    axios
      .post("nextgen/authenticate")
      .then(() => {
        if (!store.state.isAuth) {
          store.commit("setAuth", true);
        }
      })
      .then(() => {
        initialPatientLoad()
          .then(() => {
            next();
          })
          .catch(() => {
            store.state.error.initialPatientLoad = true;
          });
      })
      .catch(() => {
        store.commit("setAuth", false);
        store.commit("erasePerson");
        next(
          Trans.i18nRoute({
            name: "landing",
            query: {
              logout: true,
            },
          })
        );
      });
  } else {
    next(); // make sure to always call next()!
  }
});
export default router;
